import { MANAGED_AI_CONNECTOR_ID } from "../../../shared/aiProviders/managedAiConnector";
import { connectorsByIdentifier } from "../../../shared/connectors";
import { managedAiModelOptions } from "../../../shared/connectors/managedAi";
import {
  managedAiDefaultPrices,
  priceKey,
} from "../../../shared/connectorUsagePricing";
import { config } from "../../environment";
import { MEASURE_UNIT } from "../../lib/connectorUsage";
import {
  FormCard,
  PriceGrid,
  PriceRow,
  ReadOnlyCell,
  SectionHeading,
} from "../AiProviderModal/modalForm";
import { Box } from "../Box";
import { ConnectorImage } from "../ConnectorImage";
import { Text } from "../Text";

const MEASURES = [
  "input_tokens",
  "cache_read_tokens",
  "cache_write_tokens",
  "output_tokens",
] as const;

const PROVIDER_IDENTIFIERS = { bedrock: "anthropic", openai: "openai" };

const gridTemplate = `minmax(160px, 1.4fr) repeat(${MEASURES.length}, minmax(0, 1fr))`;

const formatUsd = (amount: number) =>
  `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })}`;

export function ManagedAiPricingSection() {
  const prices = managedAiDefaultPrices(config.awsRegion);
  const models = managedAiModelOptions.filter(({ name }) =>
    config.managedAiConnectorModels.includes(name)
  );
  const price = (measure: string, model: string) => {
    const perUnit = prices.get(
      priceKey(MANAGED_AI_CONNECTOR_ID, measure, model)
    );
    return perUnit == null
      ? "—"
      : formatUsd(perUnit * (MEASURE_UNIT[measure]?.per ?? 1));
  };

  return (
    <FormCard style={{ gap: 24 }}>
      <SectionHeading
        title="Model pricing"
        description="3B sets these rates for managed models. They drive the spend shown in Monitoring and against the spend limits below. Requests over a model’s long-context threshold cost more."
      />
      <PriceGrid>
        <PriceRow style={{ gridTemplateColumns: gridTemplate }}>
          <Box left={8}>
            <Text variant="text-small-md">Model</Text>
          </Box>
          {MEASURES.map((measure) => (
            <Box key={measure} left={8}>
              <Text variant="text-small-md">
                {MEASURE_UNIT[measure]?.name} ({MEASURE_UNIT[measure]?.label})
              </Text>
            </Box>
          ))}
        </PriceRow>
        {models.map(({ name, provider }) => {
          const providerType =
            connectorsByIdentifier[PROVIDER_IDENTIFIERS[provider]]!;
          return (
            <PriceRow key={name} style={{ gridTemplateColumns: gridTemplate }}>
              <ReadOnlyCell
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <ConnectorImage
                  src={providerType.logo}
                  alt={providerType.name}
                  style={{ width: 16, height: 16, flexShrink: 0 }}
                />
                {name}
              </ReadOnlyCell>
              {MEASURES.map((measure) => (
                <ReadOnlyCell key={measure}>
                  {price(measure, name)}
                </ReadOnlyCell>
              ))}
            </PriceRow>
          );
        })}
      </PriceGrid>
    </FormCard>
  );
}
